/* eslint-disable camelcase */
import React from 'react'
import { useSchemaAction, useSchemaState, useUpdateSchemaContext } from 'contexts'
import { customPanelTitles } from 'utils/constants/customPanelTitles'
import { defaultSchema } from 'utils/constants'
import { Customizations } from 'models/schema'
import { Reorder } from 'framer-motion'
import PanelButtonBar from 'components/common/PanelButtonBar'
import useGetCurrentCustomization from 'utils/hooks/useGetCurrentCustomization'

type CustomColumnsPanelProps = {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const CustomColumnsPanel = ({ setOpen }: CustomColumnsPanelProps) => {
  const checkboxSize = '1.4rem'
  const { currentTableKey, schema } = useSchemaState()
  const { currentCustomization: hookCustomization } = useGetCurrentCustomization()
  const { setSchema, setOrder } = useSchemaAction()
  const { mutate, requiresSingleSelection } = useUpdateSchemaContext()

  const [panelCustomization, setPanelCustomization] =
    React.useState<Customizations>(hookCustomization)

  // framer motion state
  const [items, setItems] = React.useState(Object.keys(panelCustomization))

  const saveSchema = () => {
    const orderedCustomization = Object.fromEntries(
      items.map((line) => [line, panelCustomization[line as keyof Customizations]]),
    ) as Customizations
    const updatedSchema = {
      ...schema,
      tableMappings: schema.tableMappings.map((mapping) =>
        mapping.tableName === currentTableKey
          ? { ...mapping, customizations: orderedCustomization }
          : mapping,
      ),
    }
    setSchema(updatedSchema)
    setOrder(items)
    mutate(updatedSchema)
  }

  const toggleLine = (line: keyof Customizations) => {
    setPanelCustomization({
      ...panelCustomization,
      [line]: {
        ...panelCustomization[line],
        isDisplayed: !panelCustomization[line]?.isDisplayed,
      },
    })
  }

  const handleCheckBoxKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    line: keyof Customizations,
  ) => {
    e.stopPropagation()
    if (e.key === 'Enter') {
      toggleLine(line)
    }
    if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const handleConfirm = () => {
    if (requiresSingleSelection) {
      return
    }

    setOpen(false)
    saveSchema()
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirm()
    }
    if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const editLine = (line: keyof Customizations, value: string) => {
    setPanelCustomization({
      ...panelCustomization,
      [line]: {
        ...panelCustomization[line],
        label: value,
      },
    })
  }

  const handleCancel = () => {
    const referenceCurrentCustomizations = schema.tableMappings.find(
      (mapping) => mapping.tableName === currentTableKey,
    )?.customizations as Customizations
    setPanelCustomization(referenceCurrentCustomizations)
    setItems(Object.keys(referenceCurrentCustomizations))
  }

  const handleDefault = () => {
    const defaultCutomization = defaultSchema.tableMappings.find(
      (mapping) => mapping.tableName === currentTableKey,
    )?.customizations as Customizations
    setPanelCustomization(defaultCutomization)
    setItems(Object.keys(defaultCutomization))
  }
  const firstColWidth = 'mr-4'
  const secondColWidth = 'w-full'
  const thirdColWidth = 'w-full'

  return (
    <div className="flex flex-col justify-start h-full">
      <div
        className="p-1 mb-4 mr-10 text-lg font-semibold"
        style={{
          minHeight: '36px',
          // 36px=button height
        }}
      >
        {`Affichage et noms de colonnes pour ${customPanelTitles[currentTableKey]}`}
      </div>
      <div className="flex p-1 mb-2 font-medium">
        <div className={`${firstColWidth} `}>Actif</div>
        <div className={`${secondColWidth}   `}>Nom Propriété</div>
        <div className={`${thirdColWidth}  `}>Nom Affiché</div>
      </div>

      <div className="h-full overflow-y-auto">
        <Reorder.Group
          values={items}
          onReorder={setItems}
        >
          {items.map((line: string) => (
            <Reorder.Item
              value={line}
              key={line}
            >
              <div className="flex p-1 my-1 border hover:bg-gray-200 cursor-grab">
                <div
                  className={`${firstColWidth} flex items-center justify-start checkboxContainer`}
                >
                  <input
                    type="checkbox"
                    checked={
                      panelCustomization[line as keyof typeof panelCustomization]?.isDisplayed
                    }
                    className=""
                    onChange={() => toggleLine(line as keyof Customizations)}
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{
                      width: checkboxSize,
                      height: checkboxSize,
                      zIndex: 10,
                      cursor: 'pointer',
                    }}
                    onKeyDown={(e) => handleCheckBoxKeyDown(e, line as keyof Customizations)}
                  />
                </div>

                <div className={`${secondColWidth} flex items-center `}>{line}</div>

                <input
                  type="text"
                  placeholder={line}
                  value={panelCustomization[line as keyof Customizations]?.label as string}
                  onChange={(e) => editLine(line as keyof Customizations, e.target.value)}
                  onPointerDown={(e) => e.stopPropagation()}
                  className={`${thirdColWidth} bg-gray-300 border border-gray-500 rounded-sm p-1`}
                  style={{ zIndex: 2 }}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </div>

      <PanelButtonBar
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onDefault={handleDefault}
        disableConfirm={
          requiresSingleSelection
            ? 'Sélectionnez un seul compte pour enregistrer les préférences de colonnes'
            : undefined
        }
      />
    </div>
  )
}

export default CustomColumnsPanel
